import { useState } from 'react';
import { PaymentMethod } from '@/features/dashboard/tourConfig/types/PaymentMethod.ts';
import {
  AcceptanceDocumentVm,
  ChangeRoute,
  CorrectionTermsVm,
} from '@/features/dashboard/bookingChanges/types/ChangePackageFlowViewModel.ts';
import { SettlementDistribution } from '@/features/dashboard/bookingChanges/types/SettlementDistribution.ts';
import { DEFAULT_SETTLEMENT_DISTRIBUTION } from '@/features/dashboard/bookingChanges/types/SettlementDistribution.utils.ts';
import {
  initialSelectedDocumentIds,
  toggleDocumentSelection,
} from '@/features/dashboard/bookingChanges/components/flow/acceptanceDocuments.ts';

export interface ChangePackageFlowSelection {
  settlementOption: SettlementDistribution;
  setSettlementOption: (option: SettlementDistribution) => void;
  changeRoute: ChangeRoute;
  setChangeRoute: (route: ChangeRoute) => void;
  selectedDocumentIds: string[];
  toggleDocument: (document: AcceptanceDocumentVm) => void;
  /** Termin linii korekty (`YYYY-MM-DD`); podpowiedź podglądu, dopóki operator nie zmieni. */
  correctionDueDate: string | null;
  setCorrectionDueDate: (dueDate: string | null) => void;
  correctionPaymentMethods: PaymentMethod[];
  toggleCorrectionPaymentMethod: (method: PaymentMethod) => void;
}

/**
 * Wybory operatora z kroku W2: rozkład dopłaty, trasa, dokumenty oraz termin
 * i metody płatności linii korekty.
 *
 * Stan stoi ponad krokami, bo dopiero w komplecie idzie w żądaniu `resolve`;
 * same kroki są bezstanowe. Wszędzie ten sam wzorzec: `null` znaczy „operator
 * jeszcze nic nie ruszył”, więc obowiązuje podpowiedź podglądu — a pusty wybór
 * jest wyborem i nie może wpaść z powrotem w podpowiedź. Wyliczenie zamiast
 * efektu synchronizującego, bo podgląd przychodzi dopiero z zapytaniem.
 */
const useChangePackageFlowSelection = (
  documents: AcceptanceDocumentVm[],
  correctionTerms: CorrectionTermsVm | null,
): ChangePackageFlowSelection => {
  const [settlementOption, setSettlementOption] =
    useState<SettlementDistribution>(DEFAULT_SETTLEMENT_DISTRIBUTION);
  const [changeRoute, setChangeRoute] = useState<ChangeRoute>(
    ChangeRoute.CLIENT_ACCEPTANCE,
  );
  const [pickedDocumentIds, setPickedDocumentIds] = useState<string[] | null>(
    null,
  );
  const [pickedDueDate, setPickedDueDate] = useState<string | null>(null);
  const [pickedMethods, setPickedMethods] = useState<PaymentMethod[] | null>(
    null,
  );

  const selectedDocumentIds =
    pickedDocumentIds ?? initialSelectedDocumentIds(documents);
  const correctionDueDate = pickedDueDate ?? correctionTerms?.dueDate ?? null;
  const correctionPaymentMethods =
    pickedMethods ?? correctionTerms?.paymentMethods ?? [];

  return {
    settlementOption,
    setSettlementOption,
    changeRoute,
    setChangeRoute,
    selectedDocumentIds,
    toggleDocument: (document: AcceptanceDocumentVm) =>
      setPickedDocumentIds(
        toggleDocumentSelection(selectedDocumentIds, document),
      ),
    correctionDueDate,
    setCorrectionDueDate: setPickedDueDate,
    correctionPaymentMethods,
    toggleCorrectionPaymentMethod: (method: PaymentMethod) =>
      setPickedMethods(
        correctionPaymentMethods.includes(method)
          ? correctionPaymentMethods.filter((candidate) => candidate !== method)
          : [...correctionPaymentMethods, method],
      ),
  };
};

export default useChangePackageFlowSelection;
